// Event colour tokens → Tailwind classes.
//
// Full class strings, never templated: Tailwind only emits classes it can see
// in source, so `bg-${token}-500` would compile to nothing.

export const EVENT_COLORS = [
  'slate', 'blue', 'teal', 'green', 'amber', 'orange', 'rose', 'violet',
] as const;
export type EventColor = (typeof EVENT_COLORS)[number];

export const EVENT_COLOR_LABEL: Record<EventColor, string> = {
  slate: 'Abu', blue: 'Biru', teal: 'Toska', green: 'Hijau',
  amber: 'Kuning', orange: 'Oranye', rose: 'Merah muda', violet: 'Ungu',
};

interface ColorClasses {
  /** Solid dot / swatch. */
  dot: string;
  /** Soft tinted background + border for a card accent strip. */
  soft: string;
  /** Text on a soft background. */
  text: string;
  /** Whether a colour was chosen at all — neutral events get no accent bar. */
  set: boolean;
}

const CLASSES: Record<EventColor, ColorClasses> = {
  slate:  { dot: 'bg-slate-500',  soft: 'bg-slate-500/10 border-slate-500/30',   text: 'text-slate-700',  set: true },
  blue:   { dot: 'bg-blue-500',   soft: 'bg-blue-500/10 border-blue-500/30',     text: 'text-blue-700',   set: true },
  teal:   { dot: 'bg-teal-500',   soft: 'bg-teal-500/10 border-teal-500/30',     text: 'text-teal-700',   set: true },
  green:  { dot: 'bg-green-500',  soft: 'bg-green-500/10 border-green-500/30',   text: 'text-green-700',  set: true },
  amber:  { dot: 'bg-amber-500',  soft: 'bg-amber-500/10 border-amber-500/30',   text: 'text-amber-700',  set: true },
  orange: { dot: 'bg-orange-500', soft: 'bg-orange-500/10 border-orange-500/30', text: 'text-orange-700', set: true },
  rose:   { dot: 'bg-rose-500',   soft: 'bg-rose-500/10 border-rose-500/30',     text: 'text-rose-700',   set: true },
  violet: { dot: 'bg-violet-500', soft: 'bg-violet-500/10 border-violet-500/30', text: 'text-violet-700', set: true },
};

const NEUTRAL: ColorClasses = {
  dot: 'bg-ink-subtle/40',
  soft: 'bg-canvas border-line',
  text: 'text-ink-subtle',
  set: false,
};

export const isEventColor = (v: unknown): v is EventColor =>
  typeof v === 'string' && (EVENT_COLORS as readonly string[]).includes(v);

export const eventColorClasses = (color?: string | null): ColorClasses =>
  isEventColor(color) ? CLASSES[color] : NEUTRAL;
